"use client";

import { IconArrowRight, IconArrowUpRight } from "@decibeltrade/react-icons";
import { trialSizeFor, USDC_SCALE } from "@decibeltrade/sdk";
import { Button } from "@decibeltrade/web-core/components";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { Card } from "@/components/card/card";
import { FFT_DETAIL_HREF } from "@/components/fft/constants";
import type { FftStep } from "@/components/fft/derive-fft-step";
import { openFtouLockModal } from "@/components/fft/ftou-lock-modal";
import { Skeleton } from "@/components/skeleton";
import { Tag } from "@/components/tag";
import { useAnalytics } from "@/contexts/analytics-context";
import { useLockCountdown } from "@/hooks/use-lock-countdown";
import { NumberFormatter } from "@/utils/number-formatter";
import { getServerTime } from "@/utils/server-time";

import type { FftCardData } from "../../types";

interface FftRewardCardProps {
  card: FftCardData;
}

export function FftRewardCard({ card }: FftRewardCardProps) {
  const t = useTranslations("rewards.fftCard");
  const tStatus = useTranslations("rewards.status");
  const tDashboard = useTranslations("firstTradeOnUs");
  const router = useRouter();

  const isPreLock = card.step === "lock";
  const isCompleted = card.step === "completed";
  const countdown = useLockCountdown(card.eligibility.activeLockUnlockAtMs, {
    showSeconds: false,
  });
  // The countdown re-render at expiry flips this live without a poll.
  const unlockAtMs = card.eligibility.activeLockUnlockAtMs;
  const isClaimable = !isPreLock && unlockAtMs !== null && getServerTime() >= unlockAtMs;

  // Funnel-top denominator: the card only renders allowlisted + eligibility-resolved.
  const analytics = useAnalytics();
  const lastShownStep = useRef<FftStep | null>(null);
  useEffect(() => {
    if (!analytics.rudder || lastShownStep.current === card.step) return;
    lastShownStep.current = card.step;
    analytics.fft.cardShown({ step: card.step });
  }, [analytics, card.step]);

  const { payoutAnchors, tierSlate } = card.eligibility;
  const maxTierDays = tierSlate.at(-1)?.durationDays ?? 7;
  const amount =
    isPreLock || isCompleted
      ? t("amountUpTo", {
          amount: NumberFormatter.usdc(
            Number(trialSizeFor(payoutAnchors.highLock, maxTierDays, payoutAnchors, tierSlate)) /
              USDC_SCALE,
            { decimals: 0 },
          ),
        })
      : t("amountLocked", {
          amount: NumberFormatter.usdc(Number(card.eligibility.activeLockedAmount) / USDC_SCALE, {
            decimals: 0,
          }),
        });

  const goToDetails = () => {
    analytics.fft.cardCtaClicked({ cta: "details", step: card.step });
    router.push(FFT_DETAIL_HREF);
  };

  const onStartTrial = () => {
    analytics.fft.cardCtaClicked({ cta: "start_trial", step: card.step });
    openFtouLockModal(analytics);
  };

  return (
    <Card
      background="default"
      className={cx(cardStyles, gradientStyles)}
      data-testid="fft-reward-card"
      data-step={card.step}
      data-claimable={isClaimable}
    >
      <div className={tagRowStyles}>
        <Tag variant="positive" className={tagLetterStyles}>
          {t("tagFundedTrial")}
        </Tag>
        {isPreLock || isCompleted || isClaimable ? (
          <Tag variant="positive" className={tagLetterStyles}>
            {tStatus("claimable")}
          </Tag>
        ) : (
          <Tag variant="gold" className={cx(tagLetterStyles, goldTagStyles)}>
            <span className={goldDotStyles} />
            {tStatus("inProgress")}
          </Tag>
        )}
      </div>

      <div className={titleStyles}>{tDashboard("pageTitle")}</div>

      <div className={subtitleStyles}>{t("subtitle")}</div>

      <div className={eyebrowStyles}>{t("eyebrow")}</div>

      <div className={amountStyles}>{amount}</div>

      <div className={spacerStyles} />

      {!isPreLock && !isCompleted && (
        <div className={creditsUsedStyles}>
          {t("creditsUsed", {
            used: card.eligibility.creditsUsed,
            total: card.eligibility.creditsGranted,
          })}
        </div>
      )}

      <div className={ctaRowStyles}>
        {isPreLock ? (
          <>
            <Button
              variant="primary"
              size="md"
              TailIcon={IconArrowRight}
              onClick={onStartTrial}
              data-testid="fft-card-start-trial"
            >
              {t("ctaStartTrial")}
            </Button>
            <Button
              variant="text"
              size="md"
              TailIcon={IconArrowUpRight}
              onClick={goToDetails}
              data-testid="fft-card-details"
            >
              {t("ctaDetails")}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="md"
              TailIcon={IconArrowUpRight}
              onClick={goToDetails}
              data-testid="fft-card-details"
            >
              {t("ctaDetails")}
            </Button>
            {isClaimable ? (
              <span className={fundsUnlockedStyles} data-testid="fft-card-funds-unlocked">
                {t("fundsUnlocked")}
              </span>
            ) : (
              countdown && (
                <span className={countdownStyles} data-testid="fft-card-countdown">
                  {t("remaining", { time: countdown })}
                </span>
              )
            )}
          </>
        )}
      </div>
    </Card>
  );
}

export function FftRewardCardSkeleton() {
  return (
    <Card
      background="default"
      className={cx(cardStyles, gradientStyles)}
      data-testid="fft-reward-card-skeleton"
    >
      <div className={tagRowStyles}>
        <Skeleton className={css({ h: "20", w: "96", rounded: "primary" })} />
        <Skeleton className={css({ h: "20", w: "88", rounded: "primary" })} />
      </div>

      <Skeleton className={css({ mt: "12", h: "20", w: "160" })} />
      <Skeleton className={css({ mt: "2", h: "16", w: "128" })} />
      <Skeleton className={css({ mt: "16", h: "14", w: "80" })} />
      <Skeleton className={css({ mt: "4", h: "32", w: "144" })} />

      <div className={spacerStyles} />

      <Skeleton className={css({ h: "16", w: "120" })} />

      <div className={ctaRowStyles}>
        <Skeleton className={css({ h: { base: "40", desktop: "32" }, w: "128", rounded: "300" })} />
        <Skeleton className={css({ h: { base: "40", desktop: "32" }, w: "96", rounded: "300" })} />
      </div>
    </Card>
  );
}

const cardStyles = css({
  display: "flex",
  flexDirection: "column",
  h: "full",
  p: "16",
  rounded: "300!",
});

const gradientStyles = css({
  bgImage: "rewardCardNonClaimable",
});

const tagRowStyles = css({
  display: "flex",
  gap: "4",
  alignItems: "center",
  justifyContent: "space-between",
});

const tagLetterStyles = css({ letterSpacing: "[1px]", whiteSpace: "nowrap" });

const goldTagStyles = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "4",
});

const goldDotStyles = css({
  w: "[6px]",
  h: "[6px]",
  rounded: "full",
  bg: "tag.text.gold",
});

const subtitleStyles = css({
  mt: "2",
  textStyle: "highlight.md",
  color: "text.tertiary",
  truncate: true,
});

const titleStyles = css({ mt: "12", textStyle: "subheading.sm", color: "text.primary" });

const eyebrowStyles = css({
  mt: "16",
  textStyle: "body.sm",
  color: "text.tertiary",
});

const amountStyles = css({
  mt: "4",
  textStyle: "subheading.xl",
  fontWeight: "light",
  color: "text.secondary",
});

const spacerStyles = css({ flexGrow: 1, minH: "24" });

const creditsUsedStyles = css({ textStyle: "highlight.md", color: "text.disabled" });

const ctaRowStyles = flex({ mt: "12", gap: "8", alignItems: "center", wrap: "wrap" });

const countdownStyles = css({ textStyle: "highlight.md", color: "tag.text.gold" });

const fundsUnlockedStyles = css({ textStyle: "highlight.md", color: "text.positive" });
